import {
  useDoManualPayin,
  useDoManualPayinWithBankAccount,
  useGetGetFamilyMemberCountries,
  useGetPaymentInfoForFamilyMemberPhoenix,
  type ValidationErrorResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import type { ResidentBalanceInfo } from '@pflegenavi/shared/api';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { useFormik } from 'formik';
import type { FC, JSX } from 'react';
import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { calculatePendingChargesAmount } from '@pflegenavi/shared/frontend';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { PaymentMethodSelectionModal } from './PaymentMethodSelectionModal';
import { useManualPaymentValidationSchema } from './useManualPaymentValidationSchema';
import { ManualPaymentCardView } from './ManualPaymentCardView';
import { PaymentBlockedByFailedCharge } from '../../payments/PaymentBlockedByFailedCharge';

interface ManualPaymentCardProps {
  disabled: boolean;
  activeBalance: number | undefined;
  mandateActive: boolean;
  hasFailedPayment: boolean;
  balanceInfo: ResidentBalanceInfo;
}

export const ManualPaymentCard: FC<ManualPaymentCardProps> = ({
  disabled,
  activeBalance,
  mandateActive,
  hasFailedPayment,
  balanceInfo,
}): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { enqueueSnackbar } = useSnackbar();

  const { familyMember } = useFMContext();
  const { selectedResident } = useInitializeFM();

  const { mutateAsync: doManualPayin, isPending: isDoingManualPayin } =
    useDoManualPayin();
  const {
    mutateAsync: doManualPayinWithBankAccount,
    isPending: isDoingManualPayinWithBankAccount,
  } = useDoManualPayinWithBankAccount();

  const { data: countries } = useGetGetFamilyMemberCountries({
    tenant: selectedResident?.tenant,
  });

  const { data: paymentInfo } = useGetPaymentInfoForFamilyMemberPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  const pendingChargesAmount = useMemo(() => {
    return calculatePendingChargesAmount(paymentInfo?.pending_charges);
  }, [paymentInfo?.pending_charges]);

  const { initialValues, validationSchema } = useManualPaymentValidationSchema({
    familyMember,
    balanceInfo,
    residentBalance: activeBalance,
  });

  const handleUseExistingMandate = async (amount: number) => {
    try {
      await doManualPayin({
        params: {
          resident_id: selectedResident?.residentId ?? '',
          tenant: selectedResident?.tenant ?? '',
        },
        body: {
          amount: amount,
        },
      });

      enqueueSnackbar(t('fm-triggered-payments.amount-screen.success'), {
        variant: 'success',
      });
    } catch (err: unknown) {
      // Check if this is a MangoPay validation error with field-specific errors
      const validationError = err as ValidationErrorResponse | undefined;
      if (validationError?.errors && Array.isArray(validationError.errors)) {
        // Show the first error in snackbar with translation
        const firstError = validationError.errors[0];
        if (firstError) {
          const translatedMessage = i18n.exists(
            `mangopay.recipient.errors.${firstError.message}`
          )
            ? t(`mangopay.recipient.errors.${firstError.message}`)
            : firstError.message;
          enqueueSnackbar(translatedMessage, { variant: 'error' });
        }
      } else {
        enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
      }
    }
  };

  const handleUseNewBankAccount = async (
    amount: number,
    bankAccount: {
      iban: string;
      first_name: string;
      last_name: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2?: string;
        country: string;
        postal_code: string;
      };
    }
  ) => {
    return await doManualPayinWithBankAccount({
      params: {
        resident_id: selectedResident?.residentId ?? '',
        tenant: selectedResident?.tenant ?? '',
      },
      body: {
        return_path: '',
        amount: amount,
        bank_account: bankAccount,
      },
    });
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: () => {
      return;
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const { setFieldValue } = formik;

  const handleChangeAmount = useCallback(
    (value: number | undefined) => {
      setFieldValue('amount', value ? value : undefined);
    },
    [setFieldValue]
  );

  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // If there's a failed payment, show the blocked component instead
  if (hasFailedPayment) {
    return <PaymentBlockedByFailedCharge />;
  }

  return (
    <>
      <ManualPaymentCardView
        disabled={disabled}
        activeBalance={activeBalance}
        isDoingManualPayin={isDoingManualPayin}
        isDoingManualPayinWithBankAccount={isDoingManualPayinWithBankAccount}
        formik={formik}
        onChangeAmount={handleChangeAmount}
        onOpenModal={handleOpenModal}
        pendingChargesAmount={pendingChargesAmount}
      />

      <PaymentMethodSelectionModal
        open={modalOpen}
        onClose={handleCloseModal}
        onUseExistingMandate={(amount) => handleUseExistingMandate(amount)}
        onUseNewBankAccount={(amount, bankAccount) =>
          handleUseNewBankAccount(amount, bankAccount)
        }
        existingMandateActive={mandateActive}
        countries={countries?.data ?? []}
        amount={formik.values.amount}
        isLoading={isDoingManualPayin || isDoingManualPayinWithBankAccount}
      />
    </>
  );
};
